import { Button } from "@/components/ui/button"
import { useCalculator, KPI_NASTUPY } from "@/contexts/CalculatorContext"
import { RotateCcwIcon, TrophyIcon, DollarSignIcon } from "lucide-react"
import { useEffect } from "react"

export function CalculatorHeader() {
  const {
    isKpiMet,
    setMargin, 
    setNastupy,
    setContracts,
    setCurrentPeriodTotal,
    currentPeriod,
    getAllPeriods,
    getNastupyPercentage,
    calculate1MarginProfit,
    calculate3ContractProfit,
  } = useCalculator()

  const periodData = getAllPeriods()
  const previousPeriod = currentPeriod > 0 ? periodData[currentPeriod - 1] : null
  const previousIsKpiMet = previousPeriod ? previousPeriod.isKpiMet : false
  const previousBonus = previousPeriod ? previousIsKpiMet ? getNastupyPercentage(previousPeriod.nastupy) : 0 : 0

  // Calculate current period totals
  const currentPeriodMarginProfit = periodData[currentPeriod] ? 
    calculate1MarginProfit(
      periodData[currentPeriod].margin,
      previousBonus,
      periodData[currentPeriod].nastupy
    ) / 2 : 0

  const currentPeriodContractProfit = periodData[currentPeriod] ?
    calculate3ContractProfit() : 0

  // Calculate previous periods total
  const periods = [6, 12, 18, 24, 30].filter(months => {
    const periodsBack = Math.floor(months / 6)
    const targetPeriodIndex = currentPeriod - periodsBack
    return targetPeriodIndex >= 0
  })

  const totalPreviousPeriodsProfits = periods.reduce((total, months) => {
    const periodsBack = Math.floor(months / 6)
    const targetPeriodIndex = currentPeriod - periodsBack
    const historicalPeriod = periodData[targetPeriodIndex]

    if (!historicalPeriod) return total

    return total + calculate1MarginProfit(
      historicalPeriod.margin,
      previousBonus,
      historicalPeriod.nastupy,
      getDiscount(months)
    )
  }, 0)

  const currentPeriodTotal = currentPeriodMarginProfit + currentPeriodContractProfit + totalPreviousPeriodsProfits

  function getDiscount(months: number) : number  {
    if ((months === 18 || months === 24) && !isKpiMet) return 0.5;
    if ((months === 30 || months === 36) && !isKpiMet) return 0.25;
    return 1;
  }

  // calculates cumulative total
  const cumulativeTotal = periodData
    .slice(0, currentPeriod + 1)
    .reduce((sum, period) => sum + (period.currentPeriodTotal || 0), 0)

  // Use useEffect to prevent infinite loop
  // useEffect only runs when its dependencies change, not on every render. 
  // The dependency array [currentPeriodMarginProfit, currentPeriodContractProfit, totalPreviousPeriodsProfits]
  // tells React "only run this effect if any of these values have changed".
  useEffect(() => {
    setCurrentPeriodTotal(currentPeriodTotal)
  }, [currentPeriodMarginProfit, currentPeriodContractProfit, totalPreviousPeriodsProfits, currentPeriod])

  const handleReset = () => {
    setMargin(0)
    setNastupy(0)
    setContracts(0)
  }

  const handleSetKpi = () => {
    setMargin(2200)
    setNastupy(KPI_NASTUPY)
    setContracts(1)
  }

  return (
    <div className="space-y-4 md:space-y-2">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-xl md:text-2xl font-bold">
          Bonus Kalkulačka 
          <span className="block md:inline text-sm md:text-base ml-0 md:ml-2">
            (KPIs {KPI_NASTUPY} nástupů + 1 NAS za půlrok)
          </span>
        </h1>
        
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 w-full md:w-auto">
          {/* Stats Blocks */}
          <div className="flex flex-col md:flex-row items-stretch gap-2 w-full md:w-auto">
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 flex-1 md:min-w-[200px]">
              <DollarSignIcon className="w-4 h-4 text-blue-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-700 whitespace-nowrap">Období:</span>
                  <span className="text-base font-bold text-blue-700 ml-2 truncate">
                    {currentPeriodTotal.toLocaleString()} <span className="text-xs">Kč</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-md px-3 py-2 flex-1 md:min-w-[200px]">
              <DollarSignIcon className="w-4 h-4 text-green-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-green-700 whitespace-nowrap">Celkem:</span>
                  <span className="text-base font-bold text-green-700 ml-2 truncate">
                    {cumulativeTotal.toLocaleString()} <span className="text-xs">Kč</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 w-full md:w-auto">
            <Button variant="outline" className="gap-2 flex-1 md:flex-initial" onClick={handleSetKpi}>
              <TrophyIcon className="w-4 h-4" />
              <span className="md:hidden">KPI</span>
              <span className="hidden md:inline">Set KPI</span>
            </Button>
            <Button variant="outline" className="gap-2 flex-1 md:flex-initial" onClick={handleReset}>
              <RotateCcwIcon className="w-4 h-4" />
              <span className="md:hidden">Reset</span>
              <span className="hidden md:inline">Reset</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
} 